import CatacombScene from 'src/app/Scenes/CatacombScene.js';
import MainScene from 'src/app/Scenes/MainScene';
import MazeScene from 'src/app/Scenes/MazeScene';
import { weatherStates } from '../WeatherService';
import { Inventory } from './Inventory';
import { InventorySlot } from './InventorySlot';
import { CustomJoystick } from 'src/classes/custom-joystick';
import { Button } from 'src/classes/Buttons/Button';
import { SettingsButton } from '../Buttons/SettingsButton';
import { Player } from '../Player';
import RestInPiecesScene from 'src/app/Scenes/RestInPiecesScene';
import PumpHouseScene from 'src/app/Scenes/PumpHouseScene';
import RexUIPlugin from 'phaser3-rex-plugins/templates/ui/ui-plugin.js';
import { GlobalService, ModeState } from 'src/app/services/global.service';
import { getCurrentUser } from 'aws-amplify/auth';
import { Preferences } from '@capacitor/preferences';
import { bounceGameObject, showToast } from '../Utilities';

export default class UIScene extends Phaser.Scene {
  gridSpacing: integer = 4;
  margin: integer = 8;
  uiScale: integer = 1.5*GlobalService.zoom;
  _tileSize: integer = 32;
  inventorySlots: Array<InventorySlot> = [];
  mainScene: MainScene | MazeScene | CatacombScene | RestInPiecesScene | PumpHouseScene;
  inventory: Inventory;
  hoverIndex: integer;
  settingsButton: SettingsButton;
  actionButton: Button;
  player: Player;
  rexUI: RexUIPlugin;
  customJoystick: CustomJoystick
  receiveSound = new Audio('assets/audio/spell3.wav');
  static icons: Map<string, Phaser.GameObjects.Image> = new Map<
    string,
    Phaser.GameObjects.Image
  >();
  static xSpacing = 60;
  static ySpacing = 50;
  static iconWidth = 25;
  static iconHeight = 25;

  constructor() {
    super('UIScene');

    // add subscriptions to display the gps icon
    GlobalService.$gpsModeState.subscribe({
      next: async state => {
        if(state === GlobalService.modeState.On){
          UIScene.showIcon('navigate', true);
        }
        else if(state === GlobalService.modeState.Off){
          UIScene.showIcon('navigate', false);
        }
      },
      error : error => {
        console.error('There was an error starting GPS mode!', error);
        UIScene.showIcon('navigate', false);
      }
    });

    // Add subscriptions to display the online mode icon
    GlobalService.$onlineModeState.subscribe({
      next: async state => {
        if(state === GlobalService.modeState.On){
          console.log("should display online icon");
          this.showOnlineIcon(true);
        }
        else if(state === GlobalService.modeState.Off){
          this.showOnlineIcon(false);
          console.log("should hide online icon");
        }
      }
    });

    GlobalService.$conversationsMetadata.subscribe({
      next: async conversations => {
        console.log('conversations in UIScene:');
        console.log(conversations);

        const { username } = await getCurrentUser();
        const hasUnreadMessages = conversations.some(conversation => {
          return conversation.userA === username ? conversation.lastViewedByA < conversation.lastMessageTimestamp : conversation.lastViewedByB < conversation.lastMessageTimestamp;
        });
        console.log('hasUnreadMessages:', hasUnreadMessages);
        if(hasUnreadMessages){
          if(!UIScene.icons['mail'].visible){
            this.receiveSound.play(); // Play receive sound
          }
          UIScene.showIcon('mail', true);
        }
        else{
          UIScene.showIcon('mail', false);
        }
      },
      error : error => {
        console.error('There was an error checking messages.', error);
      }
    });
  }

  public static preload(scene: Phaser.Scene) {
    const scale = 0.1;

    scene.load.svg(
      'settings_icon',
      'assets/icons/md-settings.svg',
      { scale: 2*scale }
    );

    scene.load.svg(
      'navigate_icon',
      'assets/icons/navigate-circle-outline.svg',
      { scale: scale }
    );
    scene.load.svg('cloudy_icon', 'assets/icons/cloudy-outline.svg', {
      scale: scale,
    });
    scene.load.svg('rainy_icon', 'assets/icons/rainy-outline.svg', {
      scale: scale,
    });
    scene.load.svg('sunny_icon', 'assets/icons/sunny-outline.svg', {
      scale: scale,
    });
    scene.load.svg(
      'thunderstorm_icon',
      'assets/icons/thunderstorm-outline.svg',
      { scale: scale }
    );
    scene.load.svg('snow_icon', 'assets/icons/snow.svg', {
      scale: scale,
    });
    scene.load.svg('globe_icon', 'assets/icons/globe-outline.svg', {
      scale: scale,
    });
    scene.load.svg('mail_icon', 'assets/icons/mail-outline.svg', {
      scale: scale,
    });
  }

  static showIcon(iconName: string, show: boolean) {
    console.log(`showIcon: ${iconName}, ${show}`);
    UIScene.icons[iconName] && UIScene.icons[iconName].setVisible(show);
  }

  public static showIconBasedOnConnectivity(connectivityStatus: ModeState, iconName?: string, ) {
    if (
      connectivityStatus === GlobalService.modeState.On ||
      connectivityStatus === GlobalService.modeState.Error
    ) {
      if (UIScene.icons[iconName] && UIScene.icons[iconName].setVisible(true)) {
        if (connectivityStatus === GlobalService.modeState.Error)
          UIScene.icons[iconName].setTint(0xff0000);
        else UIScene.icons[iconName].setTint(0xffffff);
      }
    } else UIScene.icons[iconName] && UIScene.icons[iconName].setVisible(false);
  }

  showOnlineIcon(show: boolean) {
    UIScene.showIcon('globe', show);
  }

  addIcons() {
    const xOffset = window.innerWidth;

    // Place weather indicators
    UIScene.icons[weatherStates.sunny] = this.add
      .image(xOffset - UIScene.xSpacing + UIScene.iconWidth, UIScene.iconHeight, 'sunny_icon')
      .setVisible(false);
    UIScene.icons[weatherStates.cloudy] = this.add
      .image(xOffset - UIScene.xSpacing + UIScene.iconWidth, UIScene.iconHeight, 'cloudy_icon')
      .setVisible(false);
    UIScene.icons[weatherStates.rainy] = this.add
      .image(xOffset - UIScene.xSpacing + UIScene.iconWidth, UIScene.iconHeight, 'rainy_icon')
      .setVisible(false);
    UIScene.icons[weatherStates.thunderstorm] = this.add
      .image(xOffset - UIScene.xSpacing + UIScene.iconWidth, UIScene.iconHeight, 'thunderstorm_icon')
      .setVisible(false);
    UIScene.icons[weatherStates.snow] = this.add
      .image(xOffset - UIScene.xSpacing + UIScene.iconWidth, UIScene.iconHeight, 'snow_icon')
      .setVisible(false);

    // Place internet icon
    UIScene.icons['globe'] = this.add
      .image(xOffset - UIScene.xSpacing + UIScene.iconWidth, UIScene.iconHeight + UIScene.ySpacing, 'globe_icon')
      .setVisible(false);

    // Place mail icon
    UIScene.icons['mail'] = this.add
    .image(xOffset - UIScene.xSpacing + UIScene.iconWidth, UIScene.iconHeight + 2*UIScene.ySpacing, 'mail_icon')
    .setVisible(false);

    // Place GPS indicator
    UIScene.icons['navigate'] = this.add
    .image(xOffset - UIScene.xSpacing + UIScene.iconWidth, UIScene.iconHeight+ 3*UIScene.ySpacing, 'navigate_icon')
    .setVisible(false);

    // Make the mail icon interactive
    UIScene.icons['mail'].setInteractive({ useHandCursor: true });
    UIScene.icons['mail'].on('pointerdown', () => {
      // Emit a custom event or call a global function
      window.dispatchEvent(new CustomEvent('open-messages-modal'));
      console.log('Mail icon clicked!');
    });

    // Make the weather icons interactive
    for (const state in weatherStates) {
      UIScene.icons[state].setInteractive({ useHandCursor: true });
      UIScene.icons[state].on('pointerdown', () => {
        // Emit a custom event or call a global function
        window.dispatchEvent(new CustomEvent('open-weather-modal'));
        console.log('Weather icon clicked!');
        // Get the latest weather data
        // WeatherService.get(true);
        // GlobalService
      });
    }
  }

  repositionIcons(){
    const xOffset = window.innerWidth;
    console.log("xOffset:", xOffset);

    // Place weather indicators
    (UIScene.icons[weatherStates.sunny] as Phaser.GameObjects.Image).setPosition(xOffset - UIScene.xSpacing + UIScene.iconWidth, UIScene.iconHeight);
    (UIScene.icons[weatherStates.cloudy] as Phaser.GameObjects.Image).setPosition(xOffset - UIScene.xSpacing + UIScene.iconWidth, UIScene.iconHeight);
    (UIScene.icons[weatherStates.rainy] as Phaser.GameObjects.Image).setPosition(xOffset - UIScene.xSpacing + UIScene.iconWidth, UIScene.iconHeight);
    (UIScene.icons[weatherStates.thunderstorm] as Phaser.GameObjects.Image).setPosition(xOffset - UIScene.xSpacing + UIScene.iconWidth, UIScene.iconHeight);
    (UIScene.icons[weatherStates.snow] as Phaser.GameObjects.Image).setPosition(xOffset - UIScene.xSpacing + UIScene.iconWidth, UIScene.iconHeight);

    // Place internet icon
    (UIScene.icons['globe'] as Phaser.GameObjects.Image).setPosition(xOffset - UIScene.xSpacing + UIScene.iconWidth, UIScene.iconHeight + UIScene.ySpacing);

    // Place mail icon
    (UIScene.icons['mail'] as Phaser.GameObjects.Image).setPosition(xOffset - UIScene.xSpacing + UIScene.iconWidth, UIScene.iconHeight + 2*UIScene.ySpacing);

    // Place GPS indicator
    (UIScene.icons['navigate'] as Phaser.GameObjects.Image).setPosition(xOffset - UIScene.xSpacing + UIScene.iconWidth, UIScene.iconHeight+ 3*UIScene.ySpacing);
  }

  init(data) {
    console.log('UIScene init');
    let { mainScene } = data;
    this.mainScene = mainScene;
    this.inventory = mainScene.player.inventory;
    Inventory.maxColumns = Inventory.maxColumns;
    Inventory.subscribe(() => this.refresh());
    InventorySlot.setupGraphics(this._tileSize, this.uiScale);
  }

  async demoSettingsButton(){
    const settingButtonHasBeenDemoed = await Preferences.get({key: "settingsButtonHasBeenDemoed"}).then((result) => {
      return result.value === 'true';
    });
  
    if(!settingButtonHasBeenDemoed) {
      // Bounce the settings button
      bounceGameObject(this.settingsButton.button);
      // Display the welcome toast.
      await showToast({message: "Welcome to Small Town RVA!<br>From the settings you'll be able to create your own profile, <rb>upload photos to the game, find friends, and send messages.<br>You can also teleport if you have enough little brown mushrooms.",
        duration: 0,
        callback: async () => {
          await Preferences.set({key: "settingsButtonHasBeenDemoed", value: 'true'});
          await this.demoJoystick();
        }
      });
    }
  }

  async demoJoystick(){
    const settingButtonHasBeenDemoed = await Preferences.get({key: "settingsButtonHasBeenDemoed"}).then((result) => {
      return result.value === 'true';
    });

    const joystickHasBeenDemoed = await Preferences.get({key: "joystickHasBeenDemoed"}).then((result) => {
      return result.value === 'true';
    });
  
    if(settingButtonHasBeenDemoed && !joystickHasBeenDemoed) {
      // Bounce the settings button
      bounceGameObject(this.customJoystick.thumb);
      bounceGameObject(this.customJoystick.base);
      // Display the welcome toast.
      await showToast({ message: "Use the joystick to move on mobile or arrow keys to on desktop.",
        duration: 0,
        callback: async () => { await Preferences.set({key: "joystickHasBeenDemoed", value: 'true'});}
      });
    }
  }

  get tileSize() {
    return this._tileSize * this.uiScale;
  }

  destroyInventorySlot(inventorySlot: InventorySlot) {
    inventorySlot.destroy();
  }

  refresh() {
    try{
      this.repositionIcons();
    }
    catch(e){
      console.log("error repositioning icons");
    }
    console.log("refreshingInventory: inventory length:", this.inventorySlots.length);
    this.inventorySlots.forEach((s) => this.destroyInventorySlot(s));
    this.inventorySlots = [];
    for (let index = 0; index < Inventory.maxColumns * Inventory.maxRows; index++) {
      let x =
        this.margin +
        this.tileSize / 2 +
        (index % Inventory.maxColumns) * (this.tileSize + this.gridSpacing);
      let y =
        this.margin +
        this.tileSize / 2 +
        Math.floor(index / Inventory.maxColumns) *
          (this.tileSize + this.gridSpacing);

      let item = Inventory.getItem(index);
      if (item) {
        let inventorySlot = new InventorySlot({
          scene: this,
          x: x,
          y: y,
          count: item.quantity,
          itemName: item.name,
          texture: item?.texture,
          animationKey: item?.animationKey 
          || null
        });
        this.inventorySlots.push(inventorySlot);
      }
    }
  }

    // Method to resize the game when the window size changes.
    onResize() {
  
      // Reposition your joystick and other UI elements.
      this.customJoystick.updatePosition();
      this.settingsButton.updatePosition();
      this.actionButton.updatePosition();

      // Refresh the inventory to allow reshaping.
      // Need to make sure that the old inventory items are destroyed.
      this.refresh();
    }

  create() {
    // Show the inventory immediately
    this.addIcons();
    this.refresh();

    // Add a joystick
    this.customJoystick = new CustomJoystick(this);
    this.mainScene.player.cursors = this.customJoystick.joystick.createCursorKeys() as Phaser.Types.Input.Keyboard.CursorKeys;
  
    // Add a settings button
    this.settingsButton = new SettingsButton(this);

    // Add an action button
    this.actionButton = new Button(this);

    // Add listener to resize the game with the window 
    // but with slight a delay to allow for settling of orientation shift.
    this.scale.on('resize', () => {
      this.time.delayedCall(150, () => this.onResize(), [], this);
    });

    // Demo features
    this.demoSettingsButton();

    // Ensure online icon is shown if online mode is already on
    if (GlobalService.$onlineModeState.getValue() === GlobalService.modeState.On) {
      this.showOnlineIcon(true);
    }
  }
}
